import { useQuery } from '@tanstack/react-query';
import { useApi } from './client.js';

/**
 * Mirror of the server-side AuthoringStandards type. Kept narrow — the
 * web side just renders this as a reference doc; strict shape checks
 * live on the server.
 */
export interface AuthoringStandards {
  version: string;
  lastUpdated: string;
  helpers: Array<{
    name: string;
    signature: string;
    description: string;
    example: string;
    category:
      | 'formatting'
      | 'arithmetic'
      | 'logic'
      | 'aggregation'
      | 'iteration'
      | 'data-shape'
      | 'links';
  }>;
  renderLibraries: Array<{
    name: 'chartjs' | 'lucide' | 'fontawesome';
    description: string;
    requiresJs: boolean;
    bundledVersion: string;
    usage: string;
  }>;
  audiences: Array<{
    name: 'internal' | 'customer' | 'partner' | 'regulator';
    description: string;
    publishRole: string | null;
    defaultOverlays: { header: boolean; footer: boolean; watermark: boolean };
  }>;
  pageOptions: {
    sizes: Array<'letter' | 'legal' | 'a4' | 'tabloid'>;
    orientations: Array<'portrait' | 'landscape'>;
    marginsInches: { min: number; max: number; default: number };
  };
  widgetTypes: Array<{
    type: 'kpi' | 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'table' | 'text';
    description: string;
    typicalSize: { w: number; h: number };
    supportsDrillThrough: boolean;
    requiresBinding: boolean;
    requiredConfigFields: string[];
    optionalConfigFields: string[];
  }>;
  aggregations: Array<{ name: string; description: string; returnsInteger: boolean }>;
  numberFormats: Array<{ name: string; description: string; example: string }>;
  filterKinds: Array<{ kind: string; description: string; defaultValueShape: string }>;
  drillTargets: {
    kinds: string[];
    paramKinds: string[];
    paramMapSyntax: { rowField: string; literal: string };
  };
  frameworks: { skeletonKinds: string[]; moduleTypes: string[] };
  layoutKinds: Array<{
    kind: 'html' | 'widgets' | 'tabular';
    surface: 'report' | 'dashboard';
    description: string;
    output: string[];
  }>;
}

export function useAuthoringStandards() {
  const api = useApi();
  return useQuery<AuthoringStandards>({
    queryKey: ['authoring-standards'],
    queryFn: () => api.fetch('/authoring/standards'),
    staleTime: 10 * 60 * 1000,
  });
}
